import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, rupee, API_BASE } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Printer, Download, ArrowLeft } from "lucide-react";

export const pdfUrl = (orderNo) => `${API_BASE}/orders/${orderNo}/invoice.pdf?token=${localStorage.getItem("anuswa_token") || ""}`;

const Invoice = () => {
  const { orderNo } = useParams();
  const { user, settings } = useStore();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get(`/orders/${orderNo}`).then((r) => setOrder(r.data)).catch(() => setErr("Invoice not found or you don't have access."));
  }, [orderNo, user]);

  if (user === null) return <div className="py-32 text-center"><p className="font-serif text-2xl text-burgundy">Please log in to view this invoice.</p><Button asChild className="mt-4 rounded-none bg-burgundy"><Link to="/login">Login</Link></Button></div>;
  if (err) return <p className="py-32 text-center text-muted-text" data-testid="invoice-error">{err}</p>;
  if (!order) return <div className="py-32 text-center font-serif text-2xl text-burgundy">Loading invoice...</div>;

  const a = order.address || {};
  const s = settings || {};
  const date = new Date(order.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-6" data-testid="invoice-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Button asChild variant="ghost" className="rounded-none"><Link to={user.role === "admin" ? "/admin" : "/account"}><ArrowLeft className="mr-2 h-4 w-4" />Back</Link></Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="rounded-none border-burgundy text-burgundy" data-testid="invoice-print-btn"><Printer className="mr-2 h-4 w-4" />Print</Button>
          <Button asChild className="rounded-none bg-burgundy"><a href={pdfUrl(order.order_no)} download data-testid="invoice-download-btn"><Download className="mr-2 h-4 w-4" />Download PDF</a></Button>
        </div>
      </div>

      <div className="border border-border bg-white p-6 md:p-10 print:border-0 print:p-0" id="invoice">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-burgundy pb-6">
          <div>
            <Logo size="md" to={null} />
            <p className="mt-3 text-xs leading-5 text-muted-text">{s.business_name}<br />{s.address}<br />Phone: {s.phone}<br />Email: {s.email}</p>
          </div>
          <div className="text-right">
            <h1 className="font-serif text-4xl text-burgundy">INVOICE</h1>
            <div className="mt-3 text-xs leading-5 text-charcoal">
              <p><span className="text-muted-text">Invoice No:</span> <span data-testid="invoice-no">INV-{order.order_no}</span></p>
              <p><span className="text-muted-text">Order No:</span> {order.order_no}</p>
              <p><span className="text-muted-text">Date:</span> {date}</p>
              <p><span className="text-muted-text">Payment:</span> {order.payment_method?.toUpperCase()} ({order.payment_status})</p>
              <p><span className="text-muted-text">Status:</span> {order.status}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-cream p-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-burgundy">Bill To / Ship To</p>
          <p className="mt-2 text-sm font-medium">{a.full_name}</p>
          <p className="text-sm text-charcoal">{a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />{a.city}, {a.state} - {a.pincode}<br />Phone: {a.phone}{order.email ? <><br />Email: {order.email}</> : null}</p>
        </div>

        <table className="mt-6 w-full text-sm">
          <thead><tr className="bg-burgundy text-left text-xs uppercase text-white"><th className="p-2.5">#</th><th className="p-2.5">Item</th><th className="p-2.5">Size / Color</th><th className="p-2.5 text-right">Qty</th><th className="p-2.5 text-right">Price</th><th className="p-2.5 text-right">Total</th></tr></thead>
          <tbody>
            {order.items.map((it, i) => (
              <tr key={i} className="border-b border-border odd:bg-white even:bg-cream" data-testid={`invoice-item-${i}`}>
                <td className="p-2.5">{i + 1}</td><td className="p-2.5">{it.name}</td>
                <td className="p-2.5 text-muted-text">{[it.size, it.color].filter(Boolean).join(" / ") || "-"}</td>
                <td className="p-2.5 text-right">{it.quantity}</td><td className="p-2.5 text-right">{rupee(it.price)}</td><td className="p-2.5 text-right">{rupee(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 ml-auto w-64 text-sm">
          <div className="flex justify-between py-1"><span>Subtotal</span><span>{rupee(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between py-1 text-green-700"><span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>- {rupee(order.discount)}</span></div>}
          <div className="flex justify-between py-1"><span>Shipping</span><span>{order.shipping ? rupee(order.shipping) : "FREE"}</span></div>
          <div className="mt-1 flex justify-between border-t border-burgundy pt-2 text-base font-semibold text-burgundy"><span>Grand Total</span><span data-testid="invoice-total">{rupee(order.total)}</span></div>
        </div>

        <p className="mt-10 text-center text-xs text-muted-text">Thank you for shopping with {s.brand_name || "ANUSWA"} — {s.tagline}.<br />This is a computer-generated invoice and does not require a signature.</p>
      </div>
    </div>
  );
};

export default Invoice;
